import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react'
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react'
import { ScoopMenu, ScoopMenuItem } from '../ScoopMenu'
import { ScoopSearchBar } from '../ScoopSearchBar/ScoopSearchBar'
import { ScoopChip } from '../ScoopChip/ScoopChip'
import styles from './ScoopSelector.module.scss'

const CHIP_GAP = 4
const MORE_INDICATOR_WIDTH = 32

/**
 * ScoopSelector - Searchable dropdown selector component with optional multiselect
 *
 * @param {string|string[]} value - Current selected value (string for single, array for multiple)
 * @param {function} onChange - Change handler (receives {target: {value}})
 * @param {Array<{label, value, leftSection?, rightSection?, disabled?, framedIcon?}>} options - Options array
 * @param {string} label - Optional label above selector
 * @param {string} placeholder - Placeholder text when no selection
 * @param {boolean} searchable - Enable search functionality
 * @param {string} searchPlaceholder - Placeholder for search input
 * @param {number} searchDebounceMs - Debounce delay for search
 * @param {string} noResultsText - Text shown when no results found
 * @param {function} filterOptions - Custom filter function (options, query) => filteredOptions
 * @param {function} renderValue - Custom render for selected value (value) => ReactNode
 * @param {function} renderOption - Custom render for each option (option) => ReactNode
 * @param {'xs'|'sm'|'md'|'lg'} size - Height size (xs=28px, sm=36px, md=43px, lg=56px)
 * @param {string} labelClassName - CSS class for label
 * @param {object} labelStyle - Inline styles for label
 * @param {string} className - Additional CSS classes for trigger
 * @param {boolean} removeMargin - Remove top margin from container
 * @param {boolean} disabled - Disable the selector
 * @param {'bottom-start'|'bottom-end'|'top-start'|'top-end'} placement - Dropdown placement
 * @param {number} maxHeight - Max height of dropdown
 * @param {boolean} multiple - Enable multiselect mode
 * @param {boolean} showChips - Show selected values as chips (only when multiple=true, defaults to true)
 * @param {boolean} chipsDeletable - Show delete (X) button on chips (only when showChips=true)
 * @param {'small'|'medium'} chipSize - Size of the chips (defaults to 'small')
 * @param {string} chipColor - Color variant for chips (default, primary, secondary, success, error, warning)
 * @param {function} renderChip - Custom render for chips (value, option, onDelete) => ReactNode
 * @param {number|'auto'} maxChipsVisible - Max chips to show (0 = show all, 'auto' = calculate based on width)
 * @param {boolean} closeOnSelect - Close dropdown after selection (defaults to false for multiple, true for single)
 * @param {boolean} chipsInline - Keep chips in single line with overflow hidden (defaults to false)
 * @param {boolean} showSelectAll - Show Select All / Clear All option at the top (only for multiple mode)
 * @param {string} selectAllLabel - Custom label for Select All (defaults to 'Select All')
 * @param {string} clearAllLabel - Custom label for Clear All (defaults to 'Clear All')
 * @param {object} menuStyle - Additional inline styles for the dropdown menu
 * @param {object} menuItemStyle - Additional inline styles for each menu item
 * @param {object} searchStyle - Additional inline styles for the search input
 * @param {boolean} disablePortal - Render menu in place without portal (use when inside transformed elements like canvas)
 */
export const ScoopSelector = ({
    value,
    onChange,
    options,
    label,
    placeholder = 'Select...',
    searchable = false,
    searchPlaceholder = 'Search...',
    searchDebounceMs = 200,
    noResultsText = 'No results found',
    filterOptions,
    renderValue,
    renderOption,
    size = 'sm',
    labelClassName = '',
    labelStyle,
    className = '',
    removeMargin = false,
    disabled = false,
    placement = 'bottom-start',
    maxHeight = 300,
    multiple = false,
    showChips = true,
    chipsDeletable = true,
    chipSize = 'small',
    chipColor = 'default',
    renderChip,
    maxChipsVisible = 0,
    closeOnSelect,
    chipsInline = false,
    showSelectAll = false,
    selectAllLabel = 'Select All',
    clearAllLabel = 'Clear All',
    menuStyle,
    menuItemStyle,
    searchStyle,
    disablePortal = false,
    ...props
}) => {
    const [open, setOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [autoChipsCount, setAutoChipsCount] = useState(0)
    const [triggerWidth, setTriggerWidth] = useState(0)
    const triggerRef = useRef(null)
    const searchInputRef = useRef(null)
    const chipsContainerRef = useRef(null)
    const chipWidthsCache = useRef(new Map())
    const containerRef = useRef(null)

    // Reset search and highlighted index when closing
    useEffect(() => {
        if (!open) {
            setSearchQuery('')
            setHighlightedIndex(-1)
        }
    }, [open])

    // Close dropdown when clicking outside (for inline menu mode)
    useEffect(() => {
        if (!open || !disablePortal) return

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open, disablePortal])

    // Normalize value for multiple mode (moved up for useLayoutEffect dependency)
    const selectedValues = multiple ? (Array.isArray(value) ? value : []) : value

    // Calculate auto chips count based on actual chip widths
    const calculateAutoChips = useCallback(() => {
        if (maxChipsVisible !== 'auto' || !chipsContainerRef.current) return

        const containerWidth = chipsContainerRef.current.getBoundingClientRect().width
        if (containerWidth === 0) return

        const chips = chipsContainerRef.current.querySelectorAll('[data-chip]')
        if (chips.length === 0) return

        // Get chip values for cache key
        const chipElements = Array.from(chips)
        const chipWidths = []

        // Measure visible chips and cache their widths
        for (let i = 0; i < chipElements.length; i++) {
            const chip = chipElements[i]
            const chipKey = chip.getAttribute('data-chip-key')
            const measuredWidth = chip.getBoundingClientRect().width

            if (measuredWidth > 0) {
                // Cache this width
                chipWidthsCache.current.set(chipKey, measuredWidth)
                chipWidths.push(measuredWidth)
            } else if (chipWidthsCache.current.has(chipKey)) {
                // Use cached width for hidden chips
                chipWidths.push(chipWidthsCache.current.get(chipKey))
            } else {
                // Fallback: estimate based on average of cached widths
                const cachedWidths = Array.from(chipWidthsCache.current.values())
                const avgWidth =
                    cachedWidths.length > 0
                        ? cachedWidths.reduce((a, b) => a + b, 0) / cachedWidths.length
                        : 80
                chipWidths.push(avgWidth)
            }
        }

        // Calculate how many chips fit in given width
        const calculateFittingChips = (availableWidth) => {
            let totalWidth = 0
            let count = 0
            for (let i = 0; i < chipWidths.length; i++) {
                const widthWithGap = count > 0 ? chipWidths[i] + CHIP_GAP : chipWidths[i]
                if (totalWidth + widthWithGap <= availableWidth) {
                    totalWidth += widthWithGap
                    count++
                } else {
                    break
                }
            }
            return count
        }

        // Check if all chips fit without needing the "+X" indicator
        const countWithFullWidth = calculateFittingChips(containerWidth)

        if (countWithFullWidth >= chipWidths.length) {
            // All chips fit, no indicator needed
            setAutoChipsCount(chipWidths.length)
        } else {
            // Not all fit, reserve space for indicator and recalculate
            const countWithIndicator = calculateFittingChips(
                containerWidth - MORE_INDICATOR_WIDTH - CHIP_GAP
            )
            setAutoChipsCount(Math.max(1, countWithIndicator))
        }
    }, [maxChipsVisible])

    useLayoutEffect(() => {
        if (maxChipsVisible !== 'auto' || !multiple || !showChips) return

        // Run after DOM updates
        calculateAutoChips()
    }, [maxChipsVisible, multiple, showChips, selectedValues, calculateAutoChips])

    useEffect(() => {
        if (maxChipsVisible !== 'auto') return

        const resizeObserver = new ResizeObserver(calculateAutoChips)
        if (triggerRef.current) {
            resizeObserver.observe(triggerRef.current)
        }

        return () => resizeObserver.disconnect()
    }, [maxChipsVisible, calculateAutoChips])

    // Calculate trigger width once when opening (not on every render)
    useEffect(() => {
        if (open && triggerRef.current) {
            setTriggerWidth(triggerRef.current.getBoundingClientRect().width)
        }
    }, [open])

    // Default filter function
    const defaultFilterOptions = (opts, query) => {
        if (!query.trim()) return opts
        return opts.filter((opt) => opt.label.toLowerCase().includes(query.toLowerCase()))
    }

    const filterFn = filterOptions || defaultFilterOptions

    const allOptions = options || []
    const filteredOptions = searchable ? filterFn(allOptions, searchQuery) : allOptions

    // Determine if we should close on select
    const shouldCloseOnSelect = closeOnSelect !== undefined ? closeOnSelect : !multiple

    // Get display label for selected value (single select mode only)
    const getSelectedLabel = () => {
        if (renderValue) {
            return renderValue(value)
        }
        if (multiple) {
            if (!selectedValues.length) return placeholder
            // For multiple mode without chips, show count or comma-separated
            return `${selectedValues.length} selected`
        }
        const selectedOption = allOptions.find((opt) => opt.value === value)
        return selectedOption ? selectedOption.label : placeholder
    }

    // Get selected option (single select)
    const getSelectedOption = () => {
        return allOptions.find((opt) => opt.value === value)
    }

    // Get option by value
    const getOptionByValue = (val) => {
        return allOptions.find((opt) => opt.value === val)
    }

    // Check if a value is selected (for multiple mode)
    const isValueSelected = (val) => {
        if (!multiple) return value === val
        return selectedValues.includes(val)
    }

    // Handle chip delete
    const handleChipDelete = (valueToRemove, e) => {
        if (e) {
            e.stopPropagation()
        }
        if (disabled) return
        const newValues = selectedValues.filter((v) => v !== valueToRemove)
        onChange({ target: { value: newValues } })
    }

    const handleSelect = (selectedValue) => {
        if (multiple) {
            // Toggle selection in multiple mode
            const isSelected = selectedValues.includes(selectedValue)
            const newValues = isSelected
                ? selectedValues.filter((v) => v !== selectedValue)
                : [...selectedValues, selectedValue]
            onChange({ target: { value: newValues } })
        } else {
            onChange({ target: { value: selectedValue } })
        }
        if (shouldCloseOnSelect) {
            setOpen(false)
        }
    }

    // Check if all options are selected
    const allSelected =
        multiple && allOptions.length > 0 && selectedValues.length === allOptions.length

    // Handle Select All / Clear All
    const handleSelectAll = () => {
        if (allSelected) {
            // Clear all
            onChange({ target: { value: [] } })
        } else {
            // Select all
            const allValues = allOptions.filter((opt) => !opt.disabled).map((opt) => opt.value)
            onChange({ target: { value: allValues } })
        }
    }

    // Render chips for multiple selection
    const renderSelectedChips = () => {
        if (!multiple || !showChips || !selectedValues.length) return null

        const isAutoMode = maxChipsVisible === 'auto'
        const effectiveMaxChips = isAutoMode ? autoChipsCount : maxChipsVisible
        const remainingCount = effectiveMaxChips > 0 ? selectedValues.length - effectiveMaxChips : 0

        const containerClassName = `${styles.chipsContainer} ${chipsInline ? styles.chipsInline : ''}`

        return (
            <div ref={chipsContainerRef} className={containerClassName}>
                {selectedValues.map((val, index) => {
                    const option = getOptionByValue(val)
                    const chipLabel = option ? option.label : val
                    // Hide chips beyond count, but keep them in DOM for measurement
                    const shouldHide = effectiveMaxChips > 0 && index >= effectiveMaxChips

                    if (renderChip) {
                        return (
                            <span
                                key={val}
                                data-chip
                                data-chip-key={val}
                                className={shouldHide ? styles.hiddenChip : undefined}
                            >
                                {renderChip(val, option, () => handleChipDelete(val))}
                            </span>
                        )
                    }

                    return (
                        <span
                            key={val}
                            data-chip
                            data-chip-key={val}
                            className={shouldHide ? styles.hiddenChip : undefined}
                        >
                            <ScoopChip
                                label={chipLabel}
                                size={chipSize}
                                color={chipColor}
                                onDelete={
                                    chipsDeletable && !disabled
                                        ? () => handleChipDelete(val)
                                        : undefined
                                }
                                onMouseDown={(e) => e.stopPropagation()}
                                className={styles.chip}
                            />
                        </span>
                    )
                })}
                {remainingCount > 0 && <span className={styles.moreChips}>+{remainingCount}</span>}
            </div>
        )
    }

    const handleToggle = () => {
        if (!disabled) {
            setOpen(!open)
        }
    }

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value)
        setHighlightedIndex(-1) // Reset highlighted index when searching
    }

    const handleKeyDown = (e) => {
        if (!open) {
            // Open on Enter or Space when closed
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setOpen(true)
            }
            return
        }

        // Navigation when open
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex((prev) => {
                    const nextIndex = prev + 1
                    return nextIndex >= filteredOptions.length ? 0 : nextIndex
                })
                break

            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex((prev) => {
                    const nextIndex = prev - 1
                    return nextIndex < 0 ? filteredOptions.length - 1 : nextIndex
                })
                break

            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    const selectedOption = filteredOptions[highlightedIndex]
                    if (!selectedOption.disabled) {
                        handleSelect(selectedOption.value)
                    }
                }
                break

            case 'Escape':
                e.preventDefault()
                setOpen(false)
                triggerRef.current?.focus()
                break

            default:
                break
        }
    }

    // Convert placement to MUI-style anchorOrigin
    const getAnchorOrigin = () => {
        const [vertical, horizontal] = placement.split('-')
        return {
            vertical: vertical === 'top' ? 'top' : 'bottom',
            horizontal: horizontal === 'start' ? 'left' : horizontal === 'end' ? 'right' : 'center',
        }
    }

    return (
        <div
            ref={containerRef}
            className={`${styles.container} ${removeMargin ? styles.noMargin : ''}`}
        >
            {label && (
                <label className={`${styles.label} ${labelClassName}`} style={labelStyle}>
                    {label}
                </label>
            )}

            <button
                ref={triggerRef}
                type="button"
                className={`${styles.trigger} ${styles[size]} ${open ? styles.open : ''} ${
                    disabled ? styles.disabled : ''
                } ${multiple && showChips && selectedValues.length ? styles.hasChips : ''} ${className}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-multiselectable={multiple}
                {...props}
            >
                <div className={styles.selectedContent}>
                    {!multiple && getSelectedOption()?.leftSection && (
                        <div className={styles.selectedLeftSection}>
                            {getSelectedOption().leftSection}
                        </div>
                    )}
                    {multiple && showChips && selectedValues.length > 0 ? (
                        renderSelectedChips()
                    ) : (
                        <span className={styles.selectedValue}>{getSelectedLabel()}</span>
                    )}
                </div>
                <CaretDownIcon size={16} weight="bold" className={styles.caretIcon} />
            </button>

            {/* When disablePortal is true, use simple CSS positioning instead of Floating UI */}
            {disablePortal ? (
                open && (
                    <div
                        className={`${styles.menu} ${styles.inlineMenu}`}
                        style={{
                            maxHeight: `${maxHeight}px`,
                            ...menuStyle,
                        }}
                    >
                        {searchable && (
                            <div className={styles.searchSection}>
                                <ScoopSearchBar
                                    ref={searchInputRef}
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder={searchPlaceholder}
                                    debounceMs={searchDebounceMs}
                                    width="100%"
                                    height="36px"
                                    autoFocus
                                    inputStyle={searchStyle}
                                />
                            </div>
                        )}

                        {multiple && showSelectAll && (
                            <div className={styles.selectAllSection}>
                                <ScoopMenuItem
                                    onClick={handleSelectAll}
                                    className={styles.selectAllItem}
                                    style={menuItemStyle}
                                >
                                    {allSelected ? clearAllLabel : selectAllLabel}
                                </ScoopMenuItem>
                            </div>
                        )}

                        <div className={styles.optionsList}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isSelected = isValueSelected(option.value)
                                    const showCheck = multiple && isSelected

                                    return (
                                        <ScoopMenuItem
                                            key={option.value}
                                            onClick={() =>
                                                !option.disabled && handleSelect(option.value)
                                            }
                                            selected={isSelected}
                                            disabled={option.disabled}
                                            leftSection={option.leftSection}
                                            rightSection={
                                                showCheck ? (
                                                    <CheckIcon
                                                        size={16}
                                                        weight="bold"
                                                        className={styles.checkIcon}
                                                    />
                                                ) : (
                                                    option.rightSection
                                                )
                                            }
                                            framedIcon={option.framedIcon}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            style={menuItemStyle}
                                        >
                                            {renderOption ? renderOption(option) : option.label}
                                        </ScoopMenuItem>
                                    )
                                })
                            ) : (
                                <div className={styles.noResults}>{noResultsText}</div>
                            )}
                        </div>
                    </div>
                )
            ) : (
                <ScoopMenu
                    anchorEl={triggerRef.current}
                    open={open}
                    onClose={() => setOpen(false)}
                    anchorOrigin={getAnchorOrigin()}
                    className={styles.menu}
                    style={{
                        maxHeight: `${maxHeight}px`,
                        width: triggerWidth > 0 ? `${triggerWidth}px` : undefined,
                        minWidth: triggerWidth > 0 ? `${triggerWidth}px` : '200px',
                        maxWidth: triggerWidth > 0 ? `${triggerWidth}px` : undefined,
                        zIndex: 1700,
                        ...menuStyle,
                    }}
                >
                    {searchable && (
                        <div className={styles.searchSection}>
                            <ScoopSearchBar
                                ref={searchInputRef}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                placeholder={searchPlaceholder}
                                debounceMs={searchDebounceMs}
                                width="100%"
                                height="36px"
                                autoFocus
                                inputStyle={searchStyle}
                            />
                        </div>
                    )}

                    {multiple && showSelectAll && (
                        <div className={styles.selectAllSection}>
                            <ScoopMenuItem
                                onClick={handleSelectAll}
                                className={styles.selectAllItem}
                                style={menuItemStyle}
                            >
                                {allSelected ? clearAllLabel : selectAllLabel}
                            </ScoopMenuItem>
                        </div>
                    )}

                    <div className={styles.optionsList}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const isSelected = isValueSelected(option.value)
                                const showCheck = multiple && isSelected

                                return (
                                    <ScoopMenuItem
                                        key={option.value}
                                        onClick={() =>
                                            !option.disabled && handleSelect(option.value)
                                        }
                                        selected={isSelected}
                                        disabled={option.disabled}
                                        leftSection={option.leftSection}
                                        rightSection={
                                            showCheck ? (
                                                <CheckIcon
                                                    size={16}
                                                    weight="bold"
                                                    className={styles.checkIcon}
                                                />
                                            ) : (
                                                option.rightSection
                                            )
                                        }
                                        framedIcon={option.framedIcon}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        style={menuItemStyle}
                                    >
                                        {renderOption ? renderOption(option) : option.label}
                                    </ScoopMenuItem>
                                )
                            })
                        ) : (
                            <div className={styles.noResults}>{noResultsText}</div>
                        )}
                    </div>
                </ScoopMenu>
            )}
        </div>
    )
}

ScoopSelector.displayName = 'ScoopSelector'
